import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';

const containerStyle = {
  padding: '40px',
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%',
  color: 'var(--text-primary)',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box'
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '20px',
  marginBottom: '40px'
};

const cardStyle = {
  background: 'var(--bg-secondary)',
  borderRadius: '8px',
  padding: '20px',
  boxShadow: '0 4px 6px var(--shadow-color)',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px'
};

const titleStyle = {
  fontSize: '18px',
  fontWeight: '600',
  color: 'var(--text-secondary)',
  marginBottom: '10px'
};

const valueStyle = {
  fontSize: '32px',
  fontWeight: 'bold',
  color: 'var(--text-primary)'
};

const subtextStyle = {
  fontSize: '14px',
  color: 'var(--text-tertiary)'
};

const progressContainerStyle = {
  width: '100%',
  height: '10px',
  background: 'var(--bg-tertiary)',
  borderRadius: '5px',
  overflow: 'hidden',
  marginTop: '5px'
};

const progressBarStyle = (percent, color) => ({
  width: `${percent}%`,
  height: '100%',
  background: color,
  transition: 'width 0.5s ease-in-out'
});

const tableWrapStyle = {
  background: 'var(--bg-secondary)',
  borderRadius: '8px',
  boxShadow: '0 4px 6px var(--shadow-color)',
  overflow: 'hidden',
  marginBottom: '30px'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse'
};

const thStyle = {
  textAlign: 'left',
  padding: '12px 16px',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-tertiary)',
  color: 'var(--text-secondary)',
  fontWeight: 600,
  fontSize: '13px'
};

const tdStyle = {
  padding: '12px 16px',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '13px',
  color: 'var(--text-primary)'
};

const heatBadgeStyle = (score) => {
  let bg = 'rgba(40, 167, 69, 0.2)';
  let color = 'var(--success-color)';
  if (score >= 70) {
    bg = 'rgba(220, 53, 69, 0.2)';
    color = 'var(--error-color)';
  } else if (score >= 40) {
    bg = 'rgba(255, 193, 7, 0.2)';
    color = 'var(--warning-color)';
  }
  return {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '999px',
    background: bg,
    color,
    fontWeight: 600,
    fontSize: '12px'
  };
};

export default function MonitoringDashboard({ onBack }) {
  const [stats, setStats] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [runQueue, setRunQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, metricsRes, queueRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/system/stats`, { headers }),
        axios.get(`${API_BASE_URL}/system/metrics`, { headers }),
        axios.get(`${API_BASE_URL}/admin/run-queue`, { headers }).catch(() => ({ data: null })),
      ]);

      setStats(statsRes.data);
      setMetrics(metricsRes.data);
      setRunQueue(queueRes.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch monitoring data", err);
      setError("Failed to load data. Ensure you are an admin.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000); // Refresh every 5 seconds
    return () => clearInterval(interval);
  }, []);

  if (loading && !stats) {
    return <div style={containerStyle}>Loading system metrics...</div>;
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={{ color: 'var(--error-color)' }}>{error}</div>
        <button onClick={onBack} style={{ marginTop: '20px', background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer' }}>
          ← Back
        </button>
      </div>
    );
  }

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    const d = Math.floor(seconds / (3600 * 24));
    const h = Math.floor((seconds % (3600 * 24)) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${d}d ${h}h ${m}m`;
  };

  const formatMs = (value) => `${Math.round(Number(value || 0))} ms`;

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px', gap: '15px' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '24px', cursor: 'pointer' }}>
          ←
        </button>
        <h1 style={{ margin: 0, fontSize: '24px' }}>System Monitoring</h1>
        <span style={{ marginLeft: 'auto', fontSize: '14px', color: 'var(--text-tertiary)' }}>
          Auto-refreshing every 5s
        </span>
      </div>

      <h2 style={titleStyle}>System Health</h2>
      <div style={gridStyle}>
        {/* CPU */}
        <div style={cardStyle}>
          <div style={titleStyle}>CPU Usage</div>
          <div style={valueStyle}>{stats.cpu.percent}%</div>
          <div style={subtextStyle}>{stats.cpu.count} Cores</div>
          <div style={progressContainerStyle}>
            <div style={progressBarStyle(stats.cpu.percent, stats.cpu.percent > 80 ? 'var(--error-color)' : 'var(--primary-color)')} />
          </div>
        </div>

        {/* Memory */}
        <div style={cardStyle}>
          <div style={titleStyle}>Memory Usage</div>
          <div style={valueStyle}>{stats.memory.percent}%</div>
          <div style={subtextStyle}>
            {formatBytes(stats.memory.used)} / {formatBytes(stats.memory.total)}
          </div>
          <div style={progressContainerStyle}>
            <div style={progressBarStyle(stats.memory.percent, stats.memory.percent > 85 ? 'var(--warning-color)' : 'var(--info-color)')} />
          </div>
        </div>

        {/* Disk */}
        <div style={cardStyle}>
          <div style={titleStyle}>Disk Usage</div>
          <div style={valueStyle}>{stats.disk.percent}%</div>
          <div style={subtextStyle}>
            {formatBytes(stats.disk.used)} / {formatBytes(stats.disk.total)}
          </div>
          <div style={progressContainerStyle}>
            <div style={progressBarStyle(stats.disk.percent, stats.disk.percent > 90 ? 'var(--error-color)' : 'var(--success-color)')} />
          </div>
        </div>

        {/* Uptime */}
        <div style={cardStyle}>
          <div style={titleStyle}>System Uptime</div>
          <div style={{ ...valueStyle, fontSize: '24px', fontWeight: 'bold' }}>
            {formatUptime(stats.uptime_seconds)}
          </div>
          <div style={subtextStyle}>Since last boot</div>
        </div>
      </div>

      <h2 style={titleStyle}>Workflow Metrics</h2>
      <div style={gridStyle}>
        {/* Active Workflows */}
        <div style={cardStyle}>
          <div style={titleStyle}>Active Workflows</div>
          <div style={valueStyle}>{metrics.workflows.active}</div>
          <div style={subtextStyle}>out of {metrics.workflows.total} total</div>
        </div>

        {/* Success Rate */}
        <div style={cardStyle}>
          <div style={titleStyle}>Success Rate (All Time)</div>
          <div style={valueStyle}>{metrics.runs.success_rate.toFixed(1)}%</div>
          <div style={subtextStyle}>
            {metrics.runs.completed} / {metrics.runs.total} runs
          </div>
          <div style={progressContainerStyle}>
            <div style={progressBarStyle(metrics.runs.success_rate, 'var(--success-color)')} />
          </div>
        </div>

        {/* Recent Activity */}
        <div style={cardStyle}>
          <div style={titleStyle}>Last 24 Hours</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--text-primary)' }}>{metrics.recent_24h.total}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Runs</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--success-color)' }}>{metrics.recent_24h.completed}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Success</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--error-color)' }}>{metrics.recent_24h.failed}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>Failed</div>
            </div>
          </div>
        </div>
      </div>

      {runQueue && (
        <>
          <h2 style={titleStyle}>Run Queue</h2>
          <div style={gridStyle}>
            <div style={cardStyle}>
              <div style={titleStyle}>Backend</div>
              <div style={{ ...valueStyle, fontSize: '24px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                {runQueue.backend}
              </div>
              <div style={subtextStyle}>
                {runQueue.backend === 'redis'
                  ? 'Multi-worker / horizontal scale'
                  : 'Single-process (asyncio.PriorityQueue)'}
              </div>
            </div>
            <div style={cardStyle}>
              <div style={titleStyle}>Waiting</div>
              <div style={valueStyle}>{runQueue.depth?.waiting ?? 0}</div>
              <div style={subtextStyle}>Runs queued, awaiting a worker</div>
            </div>
            <div style={cardStyle}>
              <div style={titleStyle}>Processing</div>
              <div style={valueStyle}>{runQueue.depth?.processing ?? 0}</div>
              <div style={subtextStyle}>
                {runQueue.backend === 'redis'
                  ? 'Runs leased to a worker (in-flight)'
                  : 'In-flight tracked outside the queue'}
              </div>
            </div>
            <div style={cardStyle}>
              <div style={titleStyle}>Workers</div>
              <div style={valueStyle}>
                {runQueue.workers_alive} / {runQueue.workers_configured}
              </div>
              <div style={subtextStyle}>
                Alive / configured · Heartbeat {runQueue.heartbeat_interval_seconds}s
              </div>
            </div>
          </div>
        </>
      )}

      <h2 style={titleStyle}>Workflow Latency & Failure</h2>
      <div style={gridStyle}>
        <div style={cardStyle}>
          <div style={titleStyle}>Latency Percentiles (All Time)</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(120px, 1fr))', gap: '8px' }}>
            <div style={subtextStyle}>P50: <strong style={{ color: 'var(--text-primary)' }}>{formatMs(metrics.workflow_latency?.p50_ms)}</strong></div>
            <div style={subtextStyle}>P95: <strong style={{ color: 'var(--text-primary)' }}>{formatMs(metrics.workflow_latency?.p95_ms)}</strong></div>
            <div style={subtextStyle}>P99: <strong style={{ color: 'var(--text-primary)' }}>{formatMs(metrics.workflow_latency?.p99_ms)}</strong></div>
            <div style={subtextStyle}>Avg: <strong style={{ color: 'var(--text-primary)' }}>{formatMs(metrics.workflow_latency?.avg_ms)}</strong></div>
          </div>
          <div style={subtextStyle}>Samples: {metrics.workflow_latency?.samples || 0}</div>
        </div>

        <div style={cardStyle}>
          <div style={titleStyle}>Failure Rate</div>
          <div style={valueStyle}>{(metrics.workflow_failures?.all_time_failure_rate_pct || 0).toFixed(1)}%</div>
          <div style={subtextStyle}>All-time workflow run failure rate</div>
          <div style={progressContainerStyle}>
            <div style={progressBarStyle(metrics.workflow_failures?.all_time_failure_rate_pct || 0, 'var(--error-color)')} />
          </div>
          <div style={subtextStyle}>
            Last 24h: {(metrics.workflow_failures?.recent_24h_failure_rate_pct || 0).toFixed(1)}%
          </div>
        </div>
      </div>

      <h2 style={titleStyle}>Top Failed Workflows</h2>
      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Workflow</th>
              <th style={thStyle}>Runs</th>
              <th style={thStyle}>Failed</th>
              <th style={thStyle}>Failure %</th>
              <th style={thStyle}>P95 Latency</th>
            </tr>
          </thead>
          <tbody>
            {(metrics.workflow_failures?.top_failed_workflows || []).length === 0 ? (
              <tr>
                <td style={{ ...tdStyle, textAlign: 'center' }} colSpan={5}>No failed workflow data yet.</td>
              </tr>
            ) : (
              (metrics.workflow_failures?.top_failed_workflows || []).map((workflow) => (
                <tr key={workflow.workflow_id}>
                  <td style={tdStyle}>{workflow.workflow_name || workflow.workflow_id}</td>
                  <td style={tdStyle}>{workflow.total_runs}</td>
                  <td style={tdStyle}>{workflow.failed_runs}</td>
                  <td style={tdStyle}>{Number(workflow.failure_rate_pct || 0).toFixed(1)}%</td>
                  <td style={tdStyle}>{formatMs(workflow.p95_latency_ms)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <h2 style={titleStyle}>Node Retry/Failure Heatmap (Last 24h)</h2>
      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Node</th>
              <th style={thStyle}>Attempts</th>
              <th style={thStyle}>Retries</th>
              <th style={thStyle}>Failures</th>
              <th style={thStyle}>Retry %</th>
              <th style={thStyle}>Failure %</th>
              <th style={thStyle}>Heat</th>
            </tr>
          </thead>
          <tbody>
            {(metrics.node_heatmap?.nodes || []).length === 0 ? (
              <tr>
                <td style={{ ...tdStyle, textAlign: 'center' }} colSpan={7}>No node heatmap data yet.</td>
              </tr>
            ) : (
              (metrics.node_heatmap?.nodes || []).slice(0, 20).map((node) => (
                <tr key={node.node_id}>
                  <td style={tdStyle}>{node.node_label || node.node_id}</td>
                  <td style={tdStyle}>{node.total_attempts}</td>
                  <td style={tdStyle}>{node.retries}</td>
                  <td style={tdStyle}>{node.failures}</td>
                  <td style={tdStyle}>{Number(node.retry_rate_pct || 0).toFixed(1)}%</td>
                  <td style={tdStyle}>{Number(node.failure_rate_pct || 0).toFixed(1)}%</td>
                  <td style={tdStyle}>
                    <span style={heatBadgeStyle(Number(node.heat_score || 0))}>
                      {Number(node.heat_score || 0).toFixed(1)}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
